import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useInView } from "react-intersection-observer";

import { ApplicationStatus } from "../ApplicationStatus";
import { ApplicationQuestions } from "../sections/ApplicationQuestions";
import { ContextQuestions } from "../sections/ContextQuestions";
import { EducationExperiences } from "../sections/EducationExperiences";
import { Interests } from "../sections/Interests";
import { PersonalDetails } from "../sections/PersonalDetails";
import { Snippets } from "../sections/Snippets";
import { WorkExperiences } from "../sections/WorkExperiences";
import { JobNoLongerAvailableModalContent } from "../components/JobNoLongerAvailableModalContent";
import { ConfirmationPopup } from "../components/ConfirmationPopup";
import { Links } from "../sections/Links";
import { ApplicationQuestionsV3 } from "../sections/ApplicationQuestionsV3";

import { palette, modularScale, pxToRem } from "@otta/design-tokens";
import { Spacing, media, Modal } from "@otta/design";
import {
  YellowSpace,
  JobCard,
  JobCardType,
} from "@otta/search/pages/Jobs/JobCard";
import { useNewAtsQuestions, useIsJobLive } from "@otta/search/utils/job";
import { pushAnalyticsEvent } from "@otta/analytics";
import { useUserProfileStatus } from "@otta/search/utils/user";
import { useQuery } from "@otta/search/apollo";
import { PublicJobDocument } from "@otta/search/schema";
import { countryToCurrency } from "@otta/search/utils/currencies";
import { Loading, Spinner } from "@otta/search/components/Loading";
import { useUserLocation } from "@otta/search/hooks/useUserLocation";

const Wrapper = styled.div<{ $visible: boolean }>`
  display: flex;
  max-width: ${pxToRem(1024)};
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
  padding-bottom: ${modularScale()};

  ${({ $visible }) => !$visible && `display: none;`}
`;

const ApplicationViewWrapper = styled(Wrapper)`
  ${media({ from: "tablet" })`
    flex-direction: row;
  `}
`;

const RightWrapper = styled.div`
  flex: 0 0 70%;
  padding: ${modularScale()} ${modularScale(-2)} 0;
  ${media({ from: "tablet" })`
    padding: 0 ${modularScale(1)} 0;
  `}
`;

const LeftWrapper = styled.div<{ $tabsEnabled: boolean }>`
  display: block;
  flex: 0 0 30%;

  padding: ${({ $tabsEnabled }) => ($tabsEnabled ? 0 : modularScale(-2))}
    ${modularScale(-2)} 0;

  ${media({ from: "tablet" })`
    display: block;
    padding: 0;
    position: sticky;
    align-self: flex-start;
    top: ${({ $tabsEnabled }) =>
      $tabsEnabled ? pxToRem(55) : modularScale(1)}};
  `}
`;

const TabLink = styled.a<{ $selected: boolean }>`
  :hover {
    cursor: pointer;
    border-bottom: 4px solid ${palette.grayscale.shade200};
  }

  text-decoration: none;
  color: ${palette.brand.black};

  ${({ $selected }) =>
    $selected &&
    `&& {
        font-weight: bold;
        border-bottom: 4px solid ${palette.brand.yellow};
      }`}
`;

const Tabs = styled.div`
  width: max-content;
  display: grid;
  gap: ${modularScale()};
  grid-template-columns: repeat(2, auto);
`;

const StickyHeader = styled.div<{ $showBorder: boolean; $jobTab: boolean }>`
  top: -1px;
  z-index: 9;
  position: sticky;

  ${media({ to: "tablet" })`
    padding: 0 ${modularScale(-2)};
    background: ${palette.grayscale.shade100};
  
    ${({ $showBorder }) =>
      $showBorder && `border-bottom: 1px solid ${palette.grayscale.shade200};`}
  `}

  ${media({ from: "tablet" })`
    padding: 0 ${pxToRem(24)};
    ${({ $jobTab }) => $jobTab && `background: ${palette.grayscale.shade100}`};
    ${({ $showBorder, $jobTab }) =>
      $showBorder &&
      $jobTab &&
      `border-bottom: 1px solid ${palette.grayscale.shade200};`}
  `}
`;

const TabWrapper = styled.div`
  max-width: ${pxToRem(1024)};
  width: 100%;
  margin: 0 auto;
  padding: ${modularScale()} 0;
`;

enum Tab {
  APPLICATION = "application",
  JOB = "job",
}

interface JobIdProps {
  jobId: string;
}

function Application({ jobId }: JobIdProps): React.ReactElement {
  const [currentTab, setTab] = useState<Tab>(Tab.APPLICATION);
  const scrollOffsets = useRef({ [Tab.APPLICATION]: 0, [Tab.JOB]: 0 });

  const { ref, entry } = useInView({
    threshold: 1,
  });

  const isHeaderStuck = !entry?.isIntersecting;

  const switchTabs = (destTab: Tab) => {
    pushAnalyticsEvent({
      eventName: "Candidate Switched Application Tabs",
      currentTab,
      destinationTab: destTab,
    });

    scrollOffsets.current[currentTab] = window.scrollY;

    setTab(destTab);
  };

  useEffect(() => {
    window.scrollTo(0, scrollOffsets.current[currentTab]);
  }, [currentTab]);

  return (
    <>
      <StickyHeader
        $jobTab={currentTab === Tab.JOB}
        $showBorder={isHeaderStuck}
        ref={ref}
      >
        <TabWrapper>
          <Tabs>
            <TabLink
              data-testid="application-tab-nav"
              $selected={currentTab === Tab.APPLICATION}
              onClick={() => switchTabs(Tab.APPLICATION)}
            >
              Application
            </TabLink>
            <TabLink
              data-testid="job-tab-nav"
              $selected={currentTab === Tab.JOB}
              onClick={() => switchTabs(Tab.JOB)}
            >
              Job details
            </TabLink>
          </Tabs>
        </TabWrapper>
      </StickyHeader>
      <ApplicationView
        tabsEnabled
        jobId={jobId}
        visible={currentTab === Tab.APPLICATION}
      />
      {currentTab === Tab.JOB && <JobView jobId={jobId} />}
    </>
  );
}

export function ApplicationView({
  jobId,
  visible,
  tabsEnabled,
}: JobIdProps & {
  visible: boolean;
  tabsEnabled: boolean;
}): React.ReactElement {
  const [dismissModal, setDismissModal] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const { profileState, completedFields } = useUserProfileStatus();
  const { isLive } = useIsJobLive(jobId);

  useEffect(() => {
    pushAnalyticsEvent({
      eventName: "Candidate Viewed Application Profile Page",
      jobId: jobId,
      profileState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completedWorkExperience = completedFields?.includes(
    "work_experience_descriptions"
  );

  const showNewAtsQuestions = useNewAtsQuestions(jobId);
  return (
    <ApplicationViewWrapper $visible={visible}>
      <LeftWrapper $tabsEnabled={tabsEnabled}>
        <ApplicationStatus
          jobId={jobId}
          isLive={isLive}
          showConfirmationPopup={() => setShowPopup(true)}
        />
      </LeftWrapper>
      <RightWrapper>
        <Spacing size={2}>
          {showNewAtsQuestions === true ? (
            <ApplicationQuestionsV3 jobId={jobId} />
          ) : showNewAtsQuestions === false ? (
            <ApplicationQuestions jobId={jobId} />
          ) : (
            <Loading />
          )}
          <WorkExperiences completedWorkExperience={completedWorkExperience} />
          <EducationExperiences />
          <Snippets />
          <ContextQuestions />
          <Interests />
          <Links />
          <PersonalDetails />
        </Spacing>
      </RightWrapper>

      <Modal
        open={!isLive && !dismissModal}
        onOpenChange={setDismissModal}
        style={{ maxWidth: pxToRem(480) }}
      >
        <JobNoLongerAvailableModalContent
          onClose={() => setDismissModal(true)}
        />
      </Modal>

      {isLive && showPopup && (
        <ConfirmationPopup jobId={jobId} close={() => setShowPopup(false)} />
      )}
    </ApplicationViewWrapper>
  );
}

function JobView({ jobId }: JobIdProps): React.ReactElement | null {
  const { country: userLocation } = useUserLocation();

  const { data, loading } = useQuery(PublicJobDocument, {
    variables: {
      externalId: jobId,
      currency: countryToCurrency(userLocation),
      prefixImage: false,
    },
  });

  if (loading) {
    return <Spinner />;
  }

  return (
    <>
      <YellowSpace />
      <JobCard
        job={data?.publicJob ?? undefined}
        type={JobCardType.application}
      />
    </>
  );
}

export default function ApplicationRoute(): React.ReactElement | null {
  const { jobId } = useParams<"jobId">();

  if (!jobId) {
    return null;
  }

  return <Application jobId={jobId} />;
}
